/**
 * The phase context — which phase of the project every surface is currently looking at
 * (`KLQ-20260827-002` §"Phase bar behaviour").
 *
 * ONE HOOK, READ BY EVERY SURFACE. The bar, the drawings tree, the tasks register and the
 * reviews queue all call `usePhaseContext(projectId)` and get the same three facts back:
 * which phases the project has, which one the viewer selected, and which one the *project*
 * treats as its default. A second implementation of any of those is a second answer to one
 * question, which is the shape retro rule 8 exists to prevent.
 *
 * FOUR DECISIONS THIS MODULE IS BUILT AROUND
 *
 * 1. **"All phases" is the absence of a filter, and it is where a first visit starts.**
 *    Not `current_phase_id` — a filter nobody chose is a drawing that is missing with no
 *    explanation, and the person who would notice is exactly the one who does not yet know
 *    the product has phases. So an unrecognised, absent or stale selection all resolve to
 *    *All*, and *All* writes no search parameter at all.
 *
 * 2. **The URL is the truth; local storage is only a seed.** `?phase=` is what the surfaces
 *    read, what a shared link carries and what survives a tab switch. `kalqa.phase.<id>` is
 *    consulted exactly once per entry — when the URL says nothing — and is written whenever
 *    the person picks. Two stores that both claimed to hold the selection would disagree the
 *    first time somebody pasted a link, and the URL has to win that argument.
 *
 * 3. **A stored id that no longer names a phase falls back to All, and is forgotten.** The
 *    phase it pointed at was deleted, or the person was moved to a project whose ladder does
 *    not contain it. Leaving the key would make the next entry pay the same round trip to the
 *    same conclusion; clearing it is what makes the fallback a one-off rather than a state.
 *
 * 4. **An archived phase is still selectable.** Archiving means *not offered for new work* —
 *    the vocabulary rule of `MCK-20260813-003` §2 — and drawings stay filed under it. A bar
 *    that refused to select it would put those drawings behind no filter at all. It greys,
 *    it is absent from "set as current" and from the pickers, and it filters like any other.
 *
 * **SEVERAL SURFACES CALL THIS AT ONCE, AND THAT HAS TO BE SAFE.** The shell, the bar and
 * whichever screen is mounted all hold their own instance of the hook on the same render, so
 * the entry effect below runs more than once per commit — as it also does under React's
 * StrictMode double-invoke. Every step of it is idempotent: writing the remembered id is the
 * same write twice, and seeding the parameter is the same `replace` navigation twice, which
 * resolves to the location the first one already asked for.
 *
 * WHAT THIS MODULE DOES NOT DO: it never issues a request when the selection changes.
 * Selecting a phase writes a search parameter and nothing else; the phase list is already in
 * the cache and the surfaces below filter what they already hold. See `PROJECT_PHASE_PARAM`'s
 * callers for the enforcement of that.
 */

import { useNavigate, useSearch } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo } from 'react';

import type { Norm, NormVersion } from '@/features/setup';

import type { ProjectPhase } from './api';
import { useProjectPhases } from './queries';

/**
 * The search parameter the selection travels in.
 *
 * TWIN: every project route's `validateSearch` in `app/router.tsx` names this string. A route
 * that forgets it is a route that drops the phase on arrival — which reads as the filter
 * randomly resetting when somebody opens a tab, and is very hard to see in a diff.
 */
export const PROJECT_PHASE_PARAM = 'phase';

/** Per project, because a person works on several and each has its own ladder. */
function storageKey(projectId: string): string {
  return `kalqa.phase.${projectId}`;
}

/**
 * The last pick, remembered per project.
 *
 * Guarded, like `lib/i18n/locales.ts`: private-mode Safari and locked-down enterprise
 * profiles throw on `localStorage` access, and a remembered convenience must never be the
 * reason a project screen fails to render.
 */
function readRemembered(projectId: string): string | null {
  try {
    return window.localStorage.getItem(storageKey(projectId));
  } catch {
    return null;
  }
}

function remember(projectId: string, phaseId: string | null): void {
  try {
    if (phaseId === null) window.localStorage.removeItem(storageKey(projectId));
    else window.localStorage.setItem(storageKey(projectId), phaseId);
  } catch {
    // Nothing to do and nothing to say. The selection still works for this visit; only the
    // memory of it is lost, and a browser that refuses storage is telling us that on purpose.
  }
}

/**
 * *"New name first, old in brackets"* — the display convention of `KLQ-20260817-001`, applied
 * in one place because it is applied on the bar, in the register, in the publish drawer and
 * in every picker.
 *
 * **It composes; it does not resolve.** `label` already carries `coalesce(label_override,
 * definition.name)` — the server did that (`ProjectPhaseResponse`), and a second `coalesce`
 * here would be the drift surface the FK-plus-override design exists to avoid.
 */
export function phaseLabel(phase: Pick<ProjectPhase, 'label' | 'legacy_name'>): string {
  const legacy = phase.legacy_name;
  return legacy === null || legacy === undefined || legacy === '' || legacy === phase.label
    ? phase.label
    : `${phase.label} (${legacy})`;
}

/**
 * The norm edition a project is pinned to, found in the catalogue.
 *
 * Here rather than in either of its two callers — the bar's badge and the "+ add phase"
 * picker — because it is the same lookup and the same `null` case: a catalogue still in
 * flight, or a project pinned to an edition this build's catalogue does not carry. Both draw
 * nothing rather than guessing, which is what keeps a stale catalogue from inventing a ladder.
 */
export function findNormEdition(
  catalogue: readonly Norm[] | undefined,
  normVersionId: string | null | undefined,
): { readonly norm: Norm; readonly version: NormVersion } | null {
  if (catalogue === undefined || normVersionId === null || normVersionId === undefined) return null;
  for (const norm of catalogue) {
    const version = (norm.versions ?? []).find((candidate) => candidate.id === normVersionId);
    if (version !== undefined) return { norm, version };
  }
  return null;
}

export interface PhaseContext {
  /**
   * Every enabled phase, in the norm's order, archived ones included. `PhaseBar` decides how
   * an archived one is drawn; this hook does not hide rows from its own consumers.
   */
  readonly phases: readonly ProjectPhase[];
  /** The viewer's selection, or `null` for *All phases* — the absence of a filter. */
  readonly selectedId: string | null;
  /** The same, resolved. `null` when nothing is selected or the selection named nothing. */
  readonly selected: ProjectPhase | null;
  /**
   * The **project's** default for new drawings, from `project.current_phase_id` mirrored onto
   * `is_current`. A different fact from `selectedId`, held by the server, seen by everybody
   * on the project — and drawn with its own mark, never with the selection's.
   */
  readonly currentPhaseId: string | null;
  /** Select one, or `null` for *All*. Writes the URL and remembers the pick. No request. */
  readonly select: (phaseId: string | null) => void;
  /** The phase list is still in flight. The bar draws its shape rather than nothing. */
  readonly isLoading: boolean;
}

export function usePhaseContext(projectId: string): PhaseContext {
  const navigate = useNavigate();
  // `strict: false` because this runs under a dozen routes and they do not share a `from`.
  // Every one of them validates `phase` (see `PROJECT_PHASE_PARAM`), so the value is a string
  // or absent; the cast below is the router's own escape hatch for a loose read.
  const search: Record<string, unknown> = useSearch({ strict: false });
  const raw = search[PROJECT_PHASE_PARAM];
  const param = typeof raw === 'string' && raw !== '' ? raw : null;

  const query = useProjectPhases(projectId);
  const phases = useMemo(() => query.data ?? [], [query.data]);

  const known = useMemo(() => new Set(phases.map((phase) => phase.id)), [phases]);
  /** The parameter, only once the list can vouch for it. An id the project does not have is
      *All* — the stale-link case, and the same answer as the stale-storage one. */
  const selectedId = param !== null && known.has(param) ? param : null;

  const write = useCallback(
    (phaseId: string | null, replace: boolean) => {
      void navigate({
        // Same route, different question. `search` is rewritten rather than merged blindly:
        // *All* must remove the key, because a `?phase=` that is present and empty is a
        // different URL from one that is absent and would survive every later `prev` spread.
        to: '.',
        search: (previous: Record<string, unknown>) => {
          const rest = Object.fromEntries(
            Object.entries(previous).filter(([key]) => key !== PROJECT_PHASE_PARAM),
          );
          return phaseId === null ? rest : { ...rest, [PROJECT_PHASE_PARAM]: phaseId };
        },
        replace,
      });
    },
    [navigate],
  );

  // Entry: seed the parameter from the remembered pick, or forget a pick that no longer
  // names anything. Both are `replace` — neither is a navigation the person performed, and a
  // back button that had to step through the app's own bookkeeping is a back button that
  // does not work (the same call the `/w/` redirects make in `app/router.tsx`).
  useEffect(() => {
    if (query.data === undefined) return;
    if (param !== null) {
      // A link somebody pasted, or a tab switch. If it names a live phase it also becomes the
      // remembered one, so the next entry without a parameter lands where this one did.
      if (known.has(param)) remember(projectId, param);
      else write(null, true);
      return;
    }
    const remembered = readRemembered(projectId);
    if (remembered === null) return;
    if (known.has(remembered)) write(remembered, true);
    else remember(projectId, null);
  }, [projectId, param, known, query.data, write]);

  const select = useCallback(
    (phaseId: string | null) => {
      remember(projectId, phaseId);
      // Not `replace`. Changing which phase you are looking at is a change of what the screen
      // is showing, and the back button undoing it is what the slicer already taught people
      // to expect of a view they can share (`features/drawings/DrawingsPage`).
      write(phaseId, false);
    },
    [projectId, write],
  );

  const selected = useMemo(
    () => phases.find((phase) => phase.id === selectedId) ?? null,
    [phases, selectedId],
  );

  const currentPhaseId = useMemo(
    () => phases.find((phase) => phase.is_current)?.id ?? null,
    [phases],
  );

  return {
    phases,
    selectedId,
    selected,
    currentPhaseId,
    select,
    isLoading: query.isPending,
  };
}
